import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Input, Space, Row, Col, Statistic,
    Modal, Form, message, Popconfirm, Select, Typography
} from 'antd';
import {
    SearchOutlined, PlusOutlined, UserOutlined, TeamOutlined,
    CheckCircleOutlined, ClockCircleOutlined, StopOutlined,
    EyeOutlined, CopyOutlined, DeleteOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import citiesService from '../../common/services/cities.service';
import './affiliates.scss';

const { Title } = Typography;
const { Option } = Select;

const AffiliatesPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [affiliates, setAffiliates] = useState([]);
    const [stats, setStats] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [filters, setFilters] = useState({ search: '', status: '' });
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createForm] = Form.useForm();
    const [creating, setCreating] = useState(false);
    const [cities, setCities] = useState([]);

    const fetchAffiliates = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            };
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await affiliatesService.getAffiliates(params);
            if (response.success) {
                setAffiliates(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination?.total || 0
                }));
            }
        } catch (error) {
            console.error('Erreur chargement affiliés:', error);
            message.error('Erreur lors du chargement des affiliés');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    const fetchStats = async () => {
        try {
            const response = await affiliatesService.getStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Erreur chargement stats:', error);
        }
    };

    useEffect(() => {
        fetchAffiliates();
    }, [fetchAffiliates]);

    useEffect(() => {
        fetchStats();
        fetchCities();
    }, []);

    const fetchCities = async () => {
        try {
            const response = await citiesService.getAll();
            if (response.success) {
                setCities(response.data || []);
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    };

    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusFilter = (value) => {
        setFilters(prev => ({ ...prev, status: value || '' }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleTableChange = (paginationConfig) => {
        setPagination(prev => ({
            ...prev,
            page: paginationConfig.current,
            limit: paginationConfig.pageSize
        }));
    };

    const handleCreateAffiliate = async (values) => {
        setCreating(true);
        try {
            const response = await affiliatesService.createAffiliate(values);
            if (response.success) {
                message.success('Affilié créé avec succès');
                setCreateModalVisible(false);
                createForm.resetFields();
                fetchAffiliates();
                fetchStats();
            } else {
                message.error(response.message || 'Erreur lors de la création');
            }
        } catch (error) {
            console.error('Erreur création affilié:', error);
            message.error(error.message || 'Erreur lors de la création');
        } finally {
            setCreating(false);
        }
    };

    const handleSuspend = async (id) => {
        try {
            const response = await affiliatesService.suspendAffiliate(id);
            if (response.success) {
                message.success('Affilié suspendu');
                fetchAffiliates();
                fetchStats();
            }
        } catch (error) {
            message.error('Erreur lors de la suspension');
        }
    };

    const handleReactivate = async (id) => {
        try {
            const response = await affiliatesService.reactivateAffiliate(id);
            if (response.success) {
                message.success('Affilié réactivé');
                fetchAffiliates();
                fetchStats();
            }
        } catch (error) {
            message.error('Erreur lors de la réactivation');
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await affiliatesService.deleteAffiliate(id);
            if (response.success) {
                message.success('Affilié supprimé avec succès');
                fetchAffiliates();
                fetchStats();
            } else {
                message.error(response.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error(error.message || 'Erreur lors de la suppression');
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        message.success('Code copié !');
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Approuvé' },
            pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'En attente' },
            suspended: { color: 'error', icon: <StopOutlined />, text: 'Suspendu' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: 'Nom',
            key: 'name',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <span>{record.firstName} {record.lastName}</span>
                </Space>
            )
        },
        {
            title: 'Téléphone',
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: 'Code Parrainage',
            key: 'referralCode',
            render: (_, record) => (
                <Space>
                    <Tag color="gold">{record.affiliateProfile?.referralCode}</Tag>
                    <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyCode(record.affiliateProfile?.referralCode)}
                    />
                </Space>
            )
        },
        {
            title: 'Parrainages',
            key: 'referrals',
            render: (_, record) => (
                <Tag color="blue">
                    <TeamOutlined /> {record.affiliateProfile?.stats?.totalReferred || 0}
                </Tag>
            )
        },
        {
            title: 'Ville',
            key: 'city',
            render: (_, record) => (
                <span>{record.city?.name || '-'}</span>
            )
        },
        {
            title: 'Statut',
            key: 'status',
            render: (_, record) => getStatusTag(record.affiliateProfile?.status)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/dashboard/affiliates/${record._id}`)}
                    >
                        Détail
                    </Button>
                    {record.affiliateProfile?.status === 'approved' && (
                        <Popconfirm
                            title="Suspendre cet affilié ?"
                            onConfirm={() => handleSuspend(record._id)}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <Button size="small" danger>Suspendre</Button>
                        </Popconfirm>
                    )}
                    {record.affiliateProfile?.status === 'suspended' && (
                        <Popconfirm
                            title="Réactiver cet affilié ?"
                            onConfirm={() => handleReactivate(record._id)}
                            okText="Oui"
                            cancelText="Non"
                        >
                            <Button size="small" type="primary">Réactiver</Button>
                        </Popconfirm>
                    )}
                    <Popconfirm
                        title="Supprimer cet affilié ?"
                        description="Cette action est irréversible."
                        onConfirm={() => handleDelete(record._id)}
                        okText="Oui"
                        cancelText="Non"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="affiliates-page">
            <div className="page-header">
                <Title level={3}>Programme Affiliés</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                >
                    Nouvel Affilié
                </Button>
            </div>

            {/* Statistiques */}
            {stats && (
                <Row gutter={16} className="stats-row">
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Total Affiliés"
                                value={stats.totalAffiliates}
                                prefix={<TeamOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Actifs"
                                value={stats.approvedAffiliates}
                                valueStyle={{ color: '#3f8600' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Candidatures"
                                value={stats.pendingApplications}
                                valueStyle={{ color: '#faad14' }}
                                prefix={<ClockCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Parrainages"
                                value={stats.totalReferrals}
                                prefix={<UserOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filtres */}
            <Card className="filters-card">
                <Space wrap>
                    <Input.Search
                        placeholder="Rechercher..."
                        allowClear
                        onSearch={handleSearch}
                        style={{ width: 250 }}
                        prefix={<SearchOutlined />}
                    />
                    <Select
                        placeholder="Statut"
                        allowClear
                        style={{ width: 150 }}
                        onChange={handleStatusFilter}
                    >
                        <Option value="approved">Approuvé</Option>
                        <Option value="pending">En attente</Option>
                        <Option value="suspended">Suspendu</Option>
                    </Select>
                </Space>
            </Card>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={affiliates}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `${total} affilié(s)`
                    }}
                    onChange={handleTableChange}
                />
            </Card>

            {/* Modal création */}
            <Modal
                title="Créer un nouvel affilié"
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                footer={null}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreateAffiliate}
                >
                    <Form.Item
                        name="firstName"
                        label="Prénom"
                        rules={[{ required: true, message: 'Prénom requis' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="lastName"
                        label="Nom"
                        rules={[{ required: true, message: 'Nom requis' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label="Téléphone"
                        rules={[{ required: true, message: 'Téléphone requis' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                    >
                        <Input type="email" />
                    </Form.Item>
                    <Form.Item
                        name="city"
                        label="Ville"
                    >
                        <Select placeholder="Sélectionnez une ville" allowClear>
                            {cities.filter(c => c.isActive).map(city => (
                                <Option key={city._id} value={city._id}>{city.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Mot de passe"
                        extra="Par défaut: 123456"
                    >
                        <Input.Password placeholder="123456" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={() => setCreateModalVisible(false)}>
                                Annuler
                            </Button>
                            <Button type="primary" htmlType="submit" loading={creating}>
                                Créer
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AffiliatesPage;
